import React from "react";

import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { Box, Grid, Stack } from "@mui/material";

import { formatNumber, formatPct } from "../LeadGeneralReportsPage.helpers";
import * as Styled from "../LeadGeneralReportsPage.styled";
import { SummaryMetrics } from "../LeadGeneralReportsPage.types";

interface Props {
    metrics: SummaryMetrics;
    isLoading?: boolean;
}

const SummaryCards: React.FC<Props> = ({ metrics, isLoading }) => {
    if (isLoading) {
        return (
            <Grid container spacing={2}>
                {[0, 1, 2, 3].map((i) => (
                    <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
                        <Styled.MetricCard elevation={0}>
                            <Styled.SkeletonBlock $h={14} />
                            <Styled.SkeletonBlock $h={36} />
                            <Styled.SkeletonBlock $h={10} />
                            <Styled.SkeletonBlock $h={6} />
                        </Styled.MetricCard>
                    </Grid>
                ))}
            </Grid>
        );
    }

    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Styled.MetricCard elevation={0}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Styled.MetricLabel>Total Leads</Styled.MetricLabel>
                        <Styled.IconBadge
                            $bg="rgba(0,0,0,0.08)"
                            $color="#000000"
                        >
                            <PeopleAltOutlinedIcon sx={{ fontSize: 20 }} />
                        </Styled.IconBadge>
                    </Stack>
                    <Styled.MetricValue>
                        {formatNumber(metrics.totalLeads)}
                    </Styled.MetricValue>
                </Styled.MetricCard>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Styled.MetricCard elevation={0}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Styled.MetricLabel>Sent</Styled.MetricLabel>
                        <Styled.IconBadge
                            $bg="rgba(0,0,0,0.08)"
                            $color="#000000"
                        >
                            <SendOutlinedIcon sx={{ fontSize: 18 }} />
                        </Styled.IconBadge>
                    </Stack>
                    <Styled.MetricValue>
                        {formatNumber(metrics.sent)}
                    </Styled.MetricValue>
                    <Box
                        sx={{
                            color: "#16A34A",
                            fontSize: 12,
                            fontWeight: 600,
                        }}
                    >
                        {formatPct(metrics.sentSuccessRate)} Success Rate
                    </Box>
                    <Styled.ProgressTrack>
                        <Styled.ProgressFill
                            $width={metrics.sentSuccessRate}
                            $color="#16A34A"
                        />
                    </Styled.ProgressTrack>
                </Styled.MetricCard>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Styled.MetricCard elevation={0}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Styled.MetricLabel>Failed</Styled.MetricLabel>
                        <Styled.IconBadge
                            $bg="rgba(0,0,0,0.08)"
                            $color="#000000"
                        >
                            <ErrorOutlineIcon sx={{ fontSize: 20 }} />
                        </Styled.IconBadge>
                    </Stack>
                    <Styled.MetricValue>
                        {formatNumber(metrics.failed)}
                    </Styled.MetricValue>
                    <Box
                        sx={{ color: "#DC2626", fontSize: 12, fontWeight: 600 }}
                    >
                        {formatPct(metrics.failedAnomalyPct)} of total leads
                    </Box>
                    <Styled.ProgressTrack>
                        <Styled.ProgressFill
                            $width={Math.min(
                                100,
                                (metrics.failed /
                                    Math.max(metrics.totalLeads, 1)) *
                                    100,
                            )}
                            $color="#DC2626"
                        />
                    </Styled.ProgressTrack>
                </Styled.MetricCard>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Styled.MetricCard elevation={0}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Styled.MetricLabel>Duplicated</Styled.MetricLabel>
                        <Styled.IconBadge
                            $bg="rgba(0,0,0,0.08)"
                            $color="#000000"
                        >
                            <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />
                        </Styled.IconBadge>
                    </Stack>
                    <Styled.MetricValue>
                        {formatNumber(metrics.duplicated)}
                    </Styled.MetricValue>
                    <Box
                        sx={{ color: "#64748B", fontSize: 12, fontWeight: 500 }}
                    >
                        {metrics.duplicatedRate > 0
                            ? `${formatPct(metrics.duplicatedRate)} Duplicate Rate`
                            : "Normal baseline"}
                    </Box>
                    <Styled.ProgressTrack>
                        <Styled.ProgressFill
                            $width={Math.min(100, metrics.duplicatedRate)}
                            $color="#94A3B8"
                        />
                    </Styled.ProgressTrack>
                </Styled.MetricCard>
            </Grid>
        </Grid>
    );
};

export default SummaryCards;
